import React, { useState, useEffect } from 'react';
import _ from 'lodash';
import styled from 'styled-components';
import { PieChart, Pie, Cell } from 'recharts';

import useColors from 'hooks/use-colors';
import useTranslation from 'hooks/use-translation';

export default function TerpenePieChart({
  terpenes,
  totalValue,
  activeTerpene,
  handleFocusOn,
  handleFocusOff,
  handleTerpeneClick,
  chartRef,
  isMobile,
}) {
  const activeTerpName = activeTerpene?.libraryTerpene?.name;
  const activeTerpValue = activeTerpene?.value;
  const [chartTerpenes, setChartTerpenes] = useState(null);
  const { t } = useTranslation();
  const colors = useColors();

  const shadowPieTerpene = [{ percentage: 100 }];
  const shadowPieColor = colors.blueGrey[95];
  const defaultPieColor = colors.blueGrey[90];
  const chartSize = 175;
  const chartInnerRadius = 60;
  const chartOuterRadius = 88;

  // Create a new array of the terpenes w/ their values as percentages of the total profile
  useEffect(() => {
    const newTerpenes = _.map(terpenes, (terpene) => {
      const relativePercent = parseFloat((terpene.value / totalValue).toFixed(2)) * 100;
      let displayPercent = null;

      // If the terpene's relative percentage (value/totalValues) is <0.5% of the total terpene profile,
      // make sure the pie chart displays it at the minimum perceptible segment size: 0.5%
      if (relativePercent < 0.5) {
        displayPercent = 0.5;
      }

      return {
        ...terpene,
        percentage: displayPercent || relativePercent,
      };
    });

    setChartTerpenes(newTerpenes);
  }, [terpenes, totalValue]);

  // Make sure the active terpene is also reflected on the pie chart after any active terp change from the Terpenes List
  useEffect(() => {
    if (isMobile && activeTerpene && activeTerpValue) {
      const matchingTerp = _.find(chartTerpenes, ['libraryTerpene.name', activeTerpName]);
      handleTerpeneClick(null, matchingTerp);
    }
  }, [activeTerpene, activeTerpValue, isMobile, chartTerpenes, activeTerpName, handleTerpeneClick]);

  // Build the active terpene label that sits in the center of the pie chart on hover
  const getActiveTerpLabel = (activeTerp) => {
    const { libraryTerpene, value = {}, unitSymbol } = activeTerp;
    const name = libraryTerpene?.name || '';
    if (!activeTerp || !value) {
      return false;
    }

    return (
      <React.Fragment>
        <Name>{name}</Name>
        <Value>{value ? `${value}${unitSymbol !== '%' ? ' ' : ''}${unitSymbol}` : '--'}</Value>
      </React.Fragment>
    );
  };

  // Chart hover on
  function handleChartEnter(data, index) {
    // Set the highlighted pie chart segment (terpene) as the active terp
    if (!isMobile) {
      handleFocusOn(chartTerpenes[index]);
    }
  }

  // Chart hover off
  function handleChartLeave() {
    if (!isMobile && activeTerpene) {
      handleFocusOff();
    }
  }

  // Chart click
  function handleChartClick(data, index, e) {
    handleTerpeneClick(e, chartTerpenes[index]);
  }

  return (
    <Container ref={chartRef} tabindex='-1' aria-hidden>
      <ChartOuter size={chartSize} onMouseLeave={handleChartLeave}>
        <PieChart width={chartSize} height={chartSize}>
          <filter id='shadow' filterUnits='userSpaceOnUse' x='-5%' y='-5%' width='200%' height='200%'>
            <feDropShadow
              dx='3'
              dy='3'
              stdDeviation='4'
              floodColor='black'
              floodOpacity='0.3'
              width='200'
              height='200'
            />
          </filter>

          {/* Shadow Pie */}
          <Pie
            data={shadowPieTerpene}
            dataKey='percentage'
            innerRadius={chartInnerRadius}
            outerRadius={chartOuterRadius}
            isAnimationActive={false}
          >
            {_.map(shadowPieTerpene, () => (
              <Cell key='shadow-segment' fill={shadowPieColor} stroke='none' />
            ))}
          </Pie>

          {/* Actual Terpenes Pie */}
          <Pie
            data={chartTerpenes}
            dataKey='percentage'
            innerRadius={chartInnerRadius}
            outerRadius={chartOuterRadius}
            isAnimationActive={false}
            paddingAngle={3}
            fill={defaultPieColor}
            onMouseEnter={handleChartEnter}
            onClick={handleChartClick}
            cursor='pointer'
          >
            {_.map(chartTerpenes, (entry, index) => {
              const { libraryTerpene, color } = entry;
              const { name } = libraryTerpene;
              const isFaded = !!(activeTerpene && activeTerpValue && activeTerpName !== name);
              const isShadowed = activeTerpene && activeTerpValue && !isFaded;

              return (
                <Cell
                  key={`cell-${index}`}
                  fill={isFaded ? defaultPieColor : color}
                  filter={isShadowed ? 'url(#shadow)' : 'none'}
                  stroke='none'
                />
              );
            })}
          </Pie>
        </PieChart>
        <LabelWrap>
          {(!activeTerpene || !activeTerpValue) && isMobile && (
            <Instruction>{t('common.tap_color_view_terpene', 'Tap a color to view terpene')}</Instruction>
          )}
          {activeTerpene && getActiveTerpLabel(activeTerpene)}
        </LabelWrap>
      </ChartOuter>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  justify-content: center;
  margin: 35px auto 44px;
  width: 100%;
  ${({ theme }) => theme.breakpoints.up('sm')} {
    margin-top: 0;
    width: 210px;
  }
  ${({ theme }) => theme.breakpoints.up('md')} {
    width: 35%;
  }
`;

const ChartOuter = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${({ size }) => size}px;
  width: ${({ size }) => size}px;

  .recharts-wrapper {
    z-index: 9;
  }
  .recharts-surface {
    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
    overflow: visible;
  }
`;

const LabelWrap = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  position: absolute;
  flex-wrap: wrap;
  width: 110px;
  height: 110px;
  padding: 0 5px;
  color: ${({ theme }) => theme.colors.grey[35]};
`;

const Name = styled.span`
  display: block;
  margin-top: 5px;
  font-weight: bold;
  line-height: 1;
  font-size: 14px;
  word-wrap: break-word;
  overflow-wrap: break-word;
  -webkit-hyphens: auto;
  -moz-hyphens: auto;
  hyphens: auto;
  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 15px;
  }
`;

const Value = styled.span`
  display: block;
  margin-top: 5px;
  font-size: 13px;
  line-height: 1;
`;

const Instruction = styled.span`
  display: block;
  font-size: 12px;
  line-height: 1.35;
  width: 80px;
  color: ${({ theme }) => theme.colors.grey[45]};
`;
